import type { ReactElement } from "react";

import { ChatGptLogoIcon } from "components/icons/ChatGptLogoIcon";
import { ClaudeLogoIcon } from "components/icons/ClaudeLogoIcon";
import { CursorLogoIcon } from "components/icons/CursorLogoIcon";
import type { McpConnectorClient } from "hooks/workspace/useMcpConnector";
import { cn } from "utils/cn";

const MARKS: Record<McpConnectorClient, { icon: ReactElement; className: string }> = {
  // Claude has a brand accent; ChatGPT and Cursor are monochrome brands.
  claude: {
    icon: <ClaudeLogoIcon className="h-4 w-4" />,
    className: "border-connector/25 bg-connector/10 text-connector",
  },
  chatgpt: {
    icon: <ChatGptLogoIcon className="h-4 w-4" />,
    className: "border-border bg-muted text-foreground",
  },
  cursor: {
    icon: <CursorLogoIcon className="h-4 w-4" />,
    className: "border-border bg-muted text-foreground",
  },
};

const HOST_CLIENTS: Array<[RegExp, McpConnectorClient]> = [
  [/(^|\.)(claude\.ai|anthropic\.com)$/, "claude"],
  [/(^|\.)(chatgpt\.com|openai\.com)$/, "chatgpt"],
  [/(^|\.)(cursor\.com|cursor\.sh)$/, "cursor"],
];

/**
 * Cursor publishes no client metadata and our server has no open registration,
 * so the backend seeds this client and the Cursor install link names it.
 */
export const CURSOR_OAUTH_CLIENT_ID = "rivetai-cursor";

/** Clients the backend seeds itself. Nobody else can create these IDs. */
const SEEDED_CLIENTS = new Map<string, McpConnectorClient>([[CURSOR_OAUTH_CLIENT_ID, "cursor"]]);

/**
 * Known app for an OAuth client ID. Matches the host that published the client
 * metadata, never the self-chosen client name, so a lookalike app gets no logo.
 */
export const aiAppFromClientId = (clientId: string): McpConnectorClient | null => {
  const seeded = SEEDED_CLIENTS.get(clientId);
  if (seeded) return seeded;
  let host: string;
  try {
    host = new URL(clientId).hostname.toLowerCase();
  } catch {
    return null;
  }
  return HOST_CLIENTS.find(([pattern]) => pattern.test(host))?.[1] ?? null;
};

type AiAppMarkProps = {
  client: McpConnectorClient | null;
  /** Initial shown for apps without a known logo. */
  fallbackName?: string;
  className?: string;
};

export const AiAppMark = ({ client, fallbackName, className }: AiAppMarkProps) => {
  const mark = client ? MARKS[client] : null;
  return (
    <span
      className={cn(
        "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border",
        mark?.className ?? "border-border bg-muted text-xs font-semibold text-muted-foreground",
        className
      )}
      aria-hidden
    >
      {mark?.icon ?? (fallbackName?.trim().charAt(0).toUpperCase() || "?")}
    </span>
  );
};
